import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import Feature from 'ol/Feature';
import WKT from 'ol/format/wkt';
import LineString from 'ol/geom/LineString';
import Point from 'ol/geom/Point';
import Polygon from 'ol/geom/Polygon';
import Draw from 'ol/interaction/Draw';
import ImageLayer from 'ol/layer/Image';
import Layer from 'ol/layer/Layer';
import TileLayer from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector';
import Map from 'ol/Map';
import Overlay from 'ol/Overlay';
import Projection from 'ol/proj/Projection';
import ImageWMS from 'ol/source/ImageWMS';
import TileWMS from 'ol/source/TileWMS';
import VectorSource from 'ol/source/Vector';
import Icon from 'ol/style/Icon';
import Stroke from 'ol/style/Stroke';
import Style from 'ol/style/Style';
import View from 'ol/View';
import { Coordinate, geom, MapBrowserPointerEvent, olx } from 'openlayers';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import { filter, map, skipUntil, takeUntil } from 'rxjs/operators';
import { AppConfig } from 'src/app/config/app-config';
import { AppConfigService } from 'src/app/config/app-config.service';
import { TekstResourcePipe } from 'src/app/core/pipes/tekstresource.pipe';
import { CloseFullscreenControl } from './models/close-fullscreen-control';
import { FullscreenControl } from './models/fullscreen-control';
import { MapCoordinate } from './models/map-coordinate';
import { MapSted } from './models/map-sted';
import { ToggleControl } from './models/toggle-control';
import { ZoomControl } from './models/zoom-control';

@Component({
  selector: 'bom-basemap',
  templateUrl: './basemap.component.html',
  styleUrls: ['./basemap.component.scss'],
})
export class BaseMapComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input()
  mapSteder$ = new BehaviorSubject<MapSted[]>([]);

  @Input()
  koordinatSted$ = new BehaviorSubject<Coordinate>(undefined);

  @Input()
  mapStedFokus$ = new Subject<MapSted>();

  @Input()
  konfliktLayers$: Observable<Layer[]> = of();

  @Input()
  enableMeasurement = false;

  @Input()
  padding = [70, 15, 15, 250];

  @Input()
  resetMap$ = new BehaviorSubject<boolean>(false);

  @Output()
  mapClick = new EventEmitter<MapCoordinate>();

  @ViewChild('mapcontainer', { static: true })
  mapcontainer: ElementRef;

  @ViewChild('measurementTooltipElement')
  measurementTooltipElement: ElementRef;

  isHelpModalOpen$: BehaviorSubject<boolean>;
  helpSections: HelpSection[];
  fullscreenControl: FullscreenControl;
  measureDistanceControl: ToggleControl;
  mapInitialized$ = new Subject<void>();
  konfliktLayers: Layer[] = [];

  private baseLayer: TileLayer;
  private baseLayerOrto: TileLayer;
  private layerWMS: TileLayer;
  private jordstykker: ImageLayer;
  private danishProjection: Projection;
  private map: Map;
  private componentDestroyed$ = new Subject();
  private matrikelGeometriLayer: VectorLayer;
  private measurementLayer: VectorLayer;
  private measurementLayerSource: VectorSource;
  private measurementLayerDraw: Draw;
  private measurementSketch: Feature;
  private measurementTooltip: Overlay;
  measurementLength: string;
  appConfig: AppConfig;

  constructor(
    private appConfigService: AppConfigService,
    private tekstResourcePipe: TekstResourcePipe
  ) {}

  ngOnInit(): void {
    this.appConfig = this.appConfigService.getConfig();
    this.setupHelpSections();
    this.isHelpModalOpen$ = new BehaviorSubject<boolean>(false);

    combineLatest([this.koordinatSted$, this.mapInitialized$])
      .pipe(
        map(x => x[0]),
        filter(koordinatSted => !!koordinatSted),
        takeUntil(this.componentDestroyed$)
      )
      .subscribe(koordinatSted => {
        this.matrikelGeometriLayer.getSource().clear();
        const location = new Point(koordinatSted);

        const iconFeature = new Feature({
          geometry: location,
        });

        const iconStyle = new Style({
          image: new Icon({
            anchor: [0.5, 1],
            anchorXUnits: 'fraction',
            anchorYUnits: 'fraction',
            src: 'assets/img/icons/map.arrow.blue.png',
          }),
        });

        iconFeature.setStyle(iconStyle);
        this.matrikelGeometriLayer.getSource().addFeature(iconFeature);
      });

    combineLatest([this.mapSteder$, this.mapInitialized$])
      .pipe(
        map(x => x[0]),
        filter(mapSteder => !!mapSteder),
        takeUntil(this.componentDestroyed$)
      )
      .subscribe(mapSteder => {
        // Draw WKT features
        this.matrikelGeometriLayer.getSource().clear();

        mapSteder.forEach(mapSted => {
          if (mapSted.geometry.geometryWktDetail != null) {
            this.matrikelGeometriLayer
              .getSource()
              .addFeature(this.getFeature(mapSted.geometry.geometryWktDetail));
          }
        });
      });

    combineLatest([this.mapStedFokus$, this.mapInitialized$])
      .pipe(
        map(x => x[0]),
        filter(mapSted => !!mapSted),
        takeUntil(this.componentDestroyed$)
      )
      .subscribe(mapSted => {
        if (mapSted.geometry.geometryWktDetail != null) {
          const feature = this.getFeature(mapSted.geometry.geometryWktDetail);
          this.fitToGeometry(feature.getGeometry() as ol.geom.SimpleGeometry, {
            padding: this.padding,
            constrainResolution: false,
          });
        }
      });

    combineLatest([this.konfliktLayers$, this.mapInitialized$])
      .pipe(
        map(x => x[0]),
        takeUntil(this.componentDestroyed$)
      )
      .subscribe(layers => {
        this.konfliktLayers.forEach(existingLayer => {
          if (!layers.some(layer => layer.get('id') === existingLayer.get('id'))) {
            const mapLayer = this.map
              .getLayers()
              .getArray()
              .find(layer => layer.get('id') === existingLayer.get('id'));
            this.map.removeLayer(mapLayer);
          }
        });

        layers.forEach(layer => {
          if (
            !this.konfliktLayers.some(existingLayer => existingLayer.get('id') === layer.get('id'))
          ) {
            this.map.addLayer(layer);
          }
        });

        this.konfliktLayers = layers;
      });

    this.resetMap$.pipe(takeUntil(this.componentDestroyed$)).subscribe(reset => {
      if (!!reset) {
        this.useDenmarkView();
      }
    });
  }

  focus() {
    this.mapcontainer.nativeElement.focus({ preventScroll: true });
  }

  ngAfterViewInit(): void {
    this.setupProjection();
    this.setupBaseMap();
    this.setupBaseMapOrto();
    this.setupJordstykker();
    this.setupLayers();

    this.initMap();
    this.setupOverlays();
    this.setupControls();
    this.setupEvents();

    this.mapInitialized$.next();
  }

  setupOverlays(): void {
    if (this.enableMeasurement) {
      this.measurementTooltip = new Overlay({
        element: this.measurementTooltipElement.nativeElement,
        offset: [0, -15],
        positioning: 'bottom-center',
      });
      this.map.addOverlay(this.measurementTooltip);
    }
  }

  get mapView() {
    return this.map.getView();
  }

  setupHelpSections(): void {
    this.helpSections = [];
    this.helpSections.push({
      header: this.tekstResourcePipe.transform('MapHelpModal.Basic.Header'),
      text: this.tekstResourcePipe.transform('MapHelpModal.Basic.Text'),
      icon: '',
      IconText: '',
    });

    this.helpSections.push({
      header: this.tekstResourcePipe.transform('MapHelpModal.SelectMatrikel.Header'),
      text: this.tekstResourcePipe.transform('MapHelpModal.SelectMatrikel.Text'),
      icon: '',
      IconText: '',
    });

    this.helpSections.push({
      header: this.tekstResourcePipe.transform('MapHelpModal.SelectMap.Header'),
      text: this.tekstResourcePipe.transform('MapHelpModal.SelectMap.Text'),
      icon: 'icon-svg icon-outline-map-24px',
      IconText: this.tekstResourcePipe.transform('MapHelpModal.SelectMap.Icon.Text'),
    });

    this.helpSections.push({
      header: this.tekstResourcePipe.transform('MapHelpModal.Copyright.Header'),
      text: this.tekstResourcePipe.transform('MapHelpModal.Copyright.Text'),
      icon: '',
      IconText: '',
    });

    this.helpSections.push({
      header: this.tekstResourcePipe.transform('MapHelpModal.FejlKilder.Header'),
      text: this.tekstResourcePipe.transform('MapHelpModal.FejlKilder.Text'),
      icon: '',
      IconText: '',
    });
  }

  setFullScreen(isFullScreen: boolean): void {
    if (isFullScreen) {
      this.mapcontainer.nativeElement.classList.remove('fullscreen');
    } else {
      this.mapcontainer.nativeElement.classList.add('fullscreen');
    }
    this.map.updateSize();
  }

  toggleWMSLayer(): void {
    this.layerWMS.setVisible(!this.layerWMS.getVisible());
  }

  togglejordstykker(): void {
    this.jordstykker.setVisible(!this.jordstykker.getVisible());
  }

  fitToGeometry(
    geometry: geom.SimpleGeometry | [number, number, number, number],
    options?: olx.view.FitOptions
  ) {
    if (options) {
      this.mapView.fit(geometry, options);
    } else {
      this.mapView.fit(geometry);
    }
  }

  private initMap(): void {
    this.map = new Map({
      controls: [],
      layers: [
        this.baseLayer,
        this.baseLayerOrto,
        this.jordstykker,
        this.layerWMS,
        this.matrikelGeometriLayer,
      ],
      target: 'map',
      keyboardEventTarget: this.mapcontainer.nativeElement,
    });
    this.useDenmarkView();
    if (this.enableMeasurement) {
      this.map.addLayer(this.measurementLayer);
    }
    this.toggleWMSLayer();
  }

  private useDenmarkView() {
    this.map.setView(
      new View({
        projection: this.danishProjection,
        center: [601710.851081, 6254262.956054],
        zoom: 2,
        minZoom: 2,
        maxZoom: 17,
      })
    );
  }

  setupControls(): void {
    const layerSwitcherControl = new ToggleControl(
      'bom-layer-switcher icon-svg',
      'icon-outline-map-24px'
    );
    const zoomInControl = new ZoomControl(
      this.map,
      1,
      'bom-zoom-in icon-svg icon-outline-add-24px'
    );
    const zoomOutControl = new ZoomControl(
      this.map,
      -1,
      'bom-zoom-out icon-svg icon-outline-remove-24px'
    );
    this.fullscreenControl = new FullscreenControl();
    const closeFullscreenControl = new CloseFullscreenControl(this.fullscreenControl.isToggled$);

    this.fullscreenControl.isToggled$
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(isToggled => {
        this.setFullScreen(!isToggled);
      });

    layerSwitcherControl.isToggled$
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(isToggled => {
        this.baseLayer.setVisible(!isToggled);
        this.baseLayerOrto.setVisible(isToggled);
      });
    this.map.addControl(closeFullscreenControl);
    this.map.addControl(zoomInControl);
    this.map.addControl(zoomOutControl);
    if (this.enableMeasurement) {
      this.addMeasurementControls();
    }
    this.map.addControl(this.fullscreenControl);
    this.map.addControl(layerSwitcherControl);
  }

  private addMeasurementControls() {
    this.measureDistanceControl = new ToggleControl(
      'bom-measure-distance icon-svg',
      'icon-measure-distance-24px',
      'icon-measure-distance-24px'
    );
    this.measureDistanceControl.isToggled$
      .pipe(skipUntil(this.mapInitialized$), takeUntil(this.componentDestroyed$))
      .subscribe(isToggled => {
        this.stopDrawing();
        if (isToggled) {
          this.startDrawing('LineString');
        }
      });
    this.map.addControl(this.measureDistanceControl);
  }

  private stopDrawing(): any {
    this.measurementLayerSource.clear();
    this.measurementTooltip.setPosition(undefined); // Hides tooltip overlay
    this.map.removeInteraction(this.measurementLayerDraw);
    if (this.measurementSketch) {
      this.measurementSketch.un('change', () => this.geometryChangedCallback); // Remove change listener
    }
  }

  private startDrawing(type: ol.geom.GeometryType): any {
    this.measurementLayerDraw = new Draw({
      source: this.measurementLayerSource,
      type: type,
      maxPoints: type === 'LineString' ? 2 : 0,
    });

    this.measurementLayerDraw.on(
      'drawstart',
      event => {
        this.measurementLayerSource.clear();
        this.measurementTooltip.setPosition(undefined); // Hides tooltip overlay
        this.measurementSketch = (event as any).feature;
        this.measurementSketch
          .getGeometry()
          .on('change', geometryChangedEvent => this.geometryChangedCallback(geometryChangedEvent));
      },
      this
    );

    this.measurementLayerDraw.on(
      'drawend',
      event => {
        this.measurementSketch.un('change', () => this.geometryChangedCallback);
      },
      this
    );

    this.map.addInteraction(this.measurementLayerDraw);
  }

  private geometryChangedCallback(event: ol.events.Event) {
    let tooltipCoord: ol.Coordinate;
    const geometry = event.target as any;
    if (geometry instanceof Polygon) {
      this.measurementLength = this.transformLength(geometry.getArea(), 'm2');
      tooltipCoord = geometry.getInteriorPoint().getCoordinates();
    } else if (geometry instanceof LineString) {
      this.measurementLength = this.transformLength(geometry.getLength(), 'm');
      tooltipCoord = geometry.getLastCoordinate();
    }
    this.measurementTooltip.setPosition(tooltipCoord);
  }

  private transformLength(length: number, lengthUnit: string): string {
    if (length > 100) {
      return Math.round((length / 1000) * 100) / 100 + 'k' + lengthUnit;
    } else {
      return Math.round(length * 100) / 100 + lengthUnit;
    }
  }

  private setupProjection(): void {
    this.danishProjection = new Projection({
      code: 'EPSG:25832',
      extent: [120000, 5661139.2, 1000000, 6500000],
      units: 'm',
    });
  }

  private setupBaseMap(): void {
    const sourceTileWMS = new TileWMS({
      urls: [this.appConfig.datafordeler.kort.mapTopo],
      params: {
        LAYERS: 'dtk_skaermkort_daempet',
        TRANSPARENT: 'TRUE',
        CRS: this.danishProjection.getCode,
        REQUEST: 'GetMap',
        FORMAT: 'image/png',
        apikey: this.appConfig.datafordeler.kort.apikey,
      },
      projection: this.danishProjection,
    });

    this.baseLayer = new TileLayer({
      extent: [120000, 5661139.2, 1000000, 6500000],
      opacity: 1.0,
      visible: true,
      source: sourceTileWMS,
    });
  }

  private setupBaseMapOrto(): void {
    const sourceTileOrtoWMS = new TileWMS({
      urls: [this.appConfig.datafordeler.kort.mapOrto],
      params: {
        LAYERS: 'orto_foraar_10',
        TRANSPARENT: 'TRUE',
        CRS: this.danishProjection.getCode,
        REQUEST: 'GetMap',
        FORMAT: 'image/png',
        APIKEY: this.appConfig.datafordeler.kort.apikey,
      },
      projection: this.danishProjection,
    });

    this.baseLayerOrto = new TileLayer({
      extent: [120000, 5661139.2, 1000000, 6500000],
      opacity: 1.0,
      visible: false,
      source: sourceTileOrtoWMS,
    });
  }

  private setupLayers(): void {
    this.layerWMS = new TileLayer({
      source: new TileWMS({
        urls: [
          'http://gwc1.plansystem.dk/gwc/service/wms',
          'http://gwc2.plansystem.dk/gwc/service/wms',
          'http://gwc3.plansystem.dk/gwc/service/wms',
          'http://gwc4.plansystem.dk/gwc/service/wms',
        ],
        params: {
          LAYERS: 'theme-pdk-kommuneplanramme-vedtaget',
          SERVICENAME: 'mat',
          TRANSPARENT: 'TRUE',
          SRS: 'EPSG:25832',
          SERVICE: 'WMS',
          REQUEST: 'GetMap',
          FORMAT: 'image/png',
          TICKET: this.appConfig.geoServiceSearchToken,
        },
        projection: this.danishProjection,
      }),
      visible: true,
    });

    this.matrikelGeometriLayer = new VectorLayer({
      source: new VectorSource(),
      style: new Style({
        stroke: new Stroke({
          color: 'rgba(0, 0, 255, 1)',
          width: 3,
        }),
      }),
    });

    if (this.enableMeasurement) {
      /** Set up measurement drawing layer */
      this.measurementLayerSource = new VectorSource();
      this.measurementLayer = new VectorLayer({
        source: this.measurementLayerSource,
        zIndex: 99,
      });
    }
  }

  private setupJordstykker(): void {
    this.jordstykker = new ImageLayer({
      source: new ImageWMS({
        url: this.appConfig.datafordeler.kort.mapJordstykker,
        params: {
          LAYERS: 'Jordstykke_Gaeldende',
          TRANSPARENT: 'TRUE',
          CRS: this.danishProjection.getCode,
          STYLES: 'Jordstykke_Gaeldende_transparent',
          REQUEST: 'GetMap',
          FORMAT: 'image/png',
          APIKEY: this.appConfig.datafordeler.kort.apikey,
        },
        ratio: 1,
        projection: this.danishProjection,
      }),
      visible: true,
      maxResolution: 3,
    });
  }

  private setupEvents(): void {
    this.map.on('click', (mapEvent: MapBrowserPointerEvent) => {
      this.focus();
      const pointerEvent = mapEvent.pointerEvent as any;
      this.mapClick.emit({
        coordinate: mapEvent.coordinate,
        multiSelect: pointerEvent.ctrlKey || pointerEvent.metaKey,
        resolution: this.map.getView().getResolution(),
      });
    });
  }

  private getFeature(geometryWKT: string): ol.Feature {
    const format = new WKT();
    return format.readFeature(geometryWKT, {
      dataProjection: this.danishProjection,
      featureProjection: this.danishProjection,
    });
  }

  setLayerVisible(isChecked: boolean, id: string) {
    const layer = this.map
      .getLayers()
      .getArray()
      .find(l => l.get('id') === id);
    if (layer) {
      layer.setVisible(isChecked);
    }
  }

  onHelpClick(isHelpModalOpen: boolean): boolean {
    this.isHelpModalOpen$.next(isHelpModalOpen);
    return false;
  }

  ngOnDestroy(): void {
    if (this.measurementSketch) {
      this.measurementSketch.un('change', () => this.geometryChangedCallback); // Remove change listener
    }
    this.componentDestroyed$.next();
    this.componentDestroyed$.unsubscribe();
  }
}

interface HelpSection {
  header: string;
  text: string;
  icon: string;
  IconText: string;
}
